import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  MenuItem,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useTagTypes,
  useTagValues,
  useCreateTagValue,
  useUpdateTagValue,
  useDeleteTagValue,
  useTagType,
} from "../../../../../api/tags/tags";
import Title from "../../../../../components/Title";
import { useSnackbar } from "../../../../../context/SnackbarContext";
import Back from "../../../../../components/Back";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";

function buildTypeTree(node) {
  if (!node) return [];
  return [
    {
      id: node.id,
      label: node.name,
      children: node.children ? node.children.map(buildTypeTree).flat() : [],
    },
  ];
}
function buildValueTree(values, typeId) {
  const items = values.filter((v) => String(v.type) === String(typeId));
  const map = {};
  items.forEach((v) => {
    map[v.id] = { ...v, children: [] };
  });
  const roots = [];
  items.forEach((v) => {
    if (v.parent && map[v.parent]) {
      map[v.parent].children.push(map[v.id]);
    } else {
      roots.push(map[v.id]);
    }
  });
  return roots;
}
function mapToTreeView(nodes, typeMap) {
  return nodes.map((node) => ({
    id: node.id,
    label: `[${typeMap[node.type] || node.type}] → ${node.name}[Tag]`,
    children:
      node.children && node.children.length
        ? mapToTreeView(node.children, typeMap)
        : undefined,
  }));
}
function flattenTree(node, acc = []) {
  acc.push({ id: node.id, label: node.name });
  if (node.children && node.children.length) {
    node.children.forEach((child) => flattenTree(child, acc));
  }
  return acc;
}

function HierarchyInputs({
  nodes,
  valuesMap,
  inputs,
  handleInputChange,
  handleAddValue,
  handleDeleteValue,
  handleEditValue,
  missingType,
  typeLevel = 0,
}) {
  return (
    <Box>
      {nodes.map((type, i) => (
        <Paper
          key={type.id}
          elevation={typeLevel === 0 ? 4 : 1}
          sx={{
            mt: 2,
            mb: 0,
            ml: typeLevel * 4,
            p: 2,
            border:
              missingType?.id === type.id
                ? "2px solid #f44336"
                : "1px solid #e0e0e0",
            background:
              typeLevel === 0
                ? "linear-gradient(90deg,#F5F6FA 40%,#F5F6FA 100%)"
                : "#fff",
            position: "relative",
            overflow: "visible",
          }}
        >
          <Box display="flex" alignItems="center" mb={1}>
            {typeLevel > 0 && (
              <SubdirectoryArrowRightIcon
                fontSize="small"
                sx={{
                  mr: 1,
                  color: "primary.light",
                  transform: "rotate(90deg)",
                  opacity: 0.6,
                }}
              />
            )}
            <FolderOpenIcon
              fontSize="small"
              sx={{ color: "primary.main", mr: 1 }}
            />
            <Typography
              fontWeight={700}
              color={
                missingType?.id === type.id ? "error.main" : "text.primary"
              }
              fontSize={typeLevel === 0 ? 18 : 16}
            >
              {type.label}
            </Typography>
            {missingType?.id === type.id && (
              <Typography ml={2} color="error" fontSize={13}>
                Required!
              </Typography>
            )}
          </Box>
          {(valuesMap[type.id] || []).map((val, idx) => (
            <Box key={idx} display="flex" alignItems="center" gap={1} mb={1}>
              <TextField
                value={val}
                onChange={(e) => handleEditValue(type.id, idx, e.target.value)}
                size="small"
                fullWidth
                sx={{ bgcolor: "#fcfcfc" }}
              />
              <Tooltip title="Remove value">
                <IconButton
                  color="error"
                  onClick={() => handleDeleteValue(type.id, idx)}
                  size="large"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              bgcolor: "#fcfcfc",
              borderRadius: 1,
              py: 1,
              pl: 2,
              mb: 1,
              border: "1px solid #f0f0f0",
            }}
          >
            <TextField
              label="Add tag"
              value={inputs[type.id] || ""}
              onChange={(e) => handleInputChange(type.id, e.target.value)}
              size="small"
              fullWidth
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddValue(type.id);
              }}
            />
            <Tooltip title="Add value">
              <span>
                <Button
                  variant="contained"
                  onClick={() => handleAddValue(type.id)}
                  disabled={!inputs[type.id]?.trim()}
                  sx={{ minWidth: 42, height: 40, px: 2 }}
                  color="primary"
                  startIcon={<AddCircleIcon />}
                >
                  Add
                </Button>
              </span>
            </Tooltip>
          </Box>
          {type.children && type.children.length > 0 && (
            <HierarchyInputs
              nodes={type.children}
              valuesMap={valuesMap}
              inputs={inputs}
              handleInputChange={handleInputChange}
              handleAddValue={handleAddValue}
              handleDeleteValue={handleDeleteValue}
              handleEditValue={handleEditValue}
              missingType={missingType}
              typeLevel={typeLevel + 1}
            />
          )}
        </Paper>
      ))}
    </Box>
  );
}

export default function CreateEditTagValuesForType() {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { search } = useLocation();
  const paramId = new URLSearchParams(search).get("id");

  const { data: types = [], isLoading: isTypesLoading } = useTagTypes();
  const {
    data: allValues = [],
    isLoading: isValuesLoading,
    refetch: refetchValues,
  } = useTagValues();

  const [selectedTypeId, setSelectedTypeId] = useState(paramId || "");
  const { data: typeTree, isLoading: isTypeLoading } = useTagType(
    selectedTypeId,
    { enabled: !!selectedTypeId }
  );

  const createTagValue = useCreateTagValue();
  const updateTagValue = useUpdateTagValue();
  const deleteTagValue = useDeleteTagValue();

  const [valuesMap, setValuesMap] = useState({});
  const [inputs, setInputs] = useState({});

  useEffect(() => {
    if (typeTree) {
      const flat = flattenTree(typeTree);
      const map = {};
      const inputInit = {};
      flat.forEach((node) => {
        const existing = allValues
          .filter(
            (val) =>
              +val.type === +node.id && (!val.parent || val.parent === null)
          )
          .map((val) => val.name);
        map[node.id] = existing;
        inputInit[node.id] = "";
      });
      setValuesMap(map);
      setInputs(inputInit);
    }
  }, [typeTree, allValues, paramId]);

  const typeHierarchy = useMemo(
    () => (typeTree ? buildTypeTree(typeTree) : []),
    [typeTree]
  );
  const flatTypes = typeTree ? flattenTree(typeTree) : [];

  const typeMap = useMemo(() => {
    const m = {};
    types.forEach((t) => (m[t.id] = t.name));
    return m;
  }, [types]);

  const missingType = flatTypes.find(
    (node) =>
      !valuesMap[node.id] ||
      !valuesMap[node.id].length ||
      !valuesMap[node.id].every((v) => v && v.trim())
  );

  const handleInputChange = (id, value) => {
    setInputs((prev) => ({ ...prev, [id]: value }));
  };
  const handleAddValue = (typeId) => {
    const value = (inputs[typeId] || "").trim();
    if (!value) return;
    setValuesMap((prev) => ({
      ...prev,
      [typeId]: [...(prev[typeId] || []), value],
    }));
    setInputs((prev) => ({ ...prev, [typeId]: "" }));
  };
  const handleDeleteValue = (typeId, idx) => {
    setValuesMap((prev) => ({
      ...prev,
      [typeId]: prev[typeId].filter((_, i) => i !== idx),
    }));
  };
  const handleEditValue = (typeId, idx, newValue) => {
    setValuesMap((prev) => ({
      ...prev,
      [typeId]: prev[typeId].map((val, i) => (i === idx ? newValue : val)),
    }));
  };

  const handleSave = async () => {
    if (missingType) {
      showSnackbar(
        `Please add at least one tag for type: "${missingType.label}"`,
        "error"
      );
      return;
    }
    try {
      for (const node of flatTypes) {
        const desired = (valuesMap[node.id] || [])
          .map((v) => v.trim())
          .filter(Boolean);
        const existObjs = allValues.filter(
          (val) =>
            +val.type === +node.id && (!val.parent || val.parent === null)
        );
        for (const old of existObjs) {
          if (!desired.includes(old.name)) {
            await deleteTagValue.mutateAsync(old.id);
          }
        }
        for (const v of desired) {
          const existing = existObjs.find((e) => e.name === v);
          if (!existing) {
            await createTagValue.mutateAsync({
              type: node.id,
              name: v,
              parent: null,
            });
          }
        }
      }
      showSnackbar("Tags saved successfully!", "success");
      await refetchValues();
      navigate(-1);
    } catch (e) {
      showSnackbar(e?.response?.data?.message || "Failed to save.", "error");
    }
  };

  const valueTree = useMemo(
    () => buildValueTree(allValues, selectedTypeId),
    [allValues, selectedTypeId]
  );
  const treeItems = useMemo(
    () => mapToTreeView(valueTree, typeMap),
    [valueTree, typeMap]
  );

  let typeField;
  if (isTypesLoading) {
    typeField = <CircularProgress size={28} sx={{ my: 2 }} />;
  } else if (paramId) {
    typeField = (
      <TextField
        label="Type"
        fullWidth
        value={
          types.find((t) => String(t.id) === String(selectedTypeId))?.name || ""
        }
        disabled
        sx={{ my: 2 }}
      />
    );
  } else {
    typeField = (
      <TextField
        select
        label="Type"
        fullWidth
        value={selectedTypeId}
        onChange={(e) => setSelectedTypeId(e.target.value)}
        sx={{ my: 2 }}
      >
        <MenuItem value="">Select...</MenuItem>
        {types
          .filter((t) => t.parent === null)
          .map((type) => (
            <MenuItem key={type.id} value={type.id}>
              {type.name}
            </MenuItem>
          ))}
      </TextField>
    );
  }

  return (
    <Box maxWidth={750} mx="auto" my={4} p={2}>
      <Title>
        {paramId
          ? "Edit Tag for Product Tag Type"
          : "Create Tag for Product Tag Type"}
      </Title>
      <Back />

      <Box my={3}>
        <Typography>Select a product tag type:</Typography>
        {typeField}
      </Box>

      {selectedTypeId && !isValuesLoading && (
        <Box my={2}>
          <Typography>Existing Tag Values Hierarchy</Typography>
          {treeItems.length ? (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: "#f9fafb",
                border: "1px solid #e0e0e0",
              }}
            >
              <RichTreeView
                items={treeItems}
                defaultExpandedItems={treeItems.map((i) => i.id)}
              />
            </Paper>
          ) : (
            <Typography color="text.secondary">
              No values yet for this type.
            </Typography>
          )}
        </Box>
      )}

      {isTypeLoading || isValuesLoading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        selectedTypeId &&
        typeHierarchy.length > 0 && (
          <Box>
            <Typography variant="subtitle1" mb={2}>
              {paramId
                ? "Update the tags for each type in the selected tag type:"
                : "Add tags for each type in the selected tag type:"}
            </Typography>

            <HierarchyInputs
              nodes={typeHierarchy}
              valuesMap={valuesMap}
              inputs={inputs}
              handleInputChange={handleInputChange}
              handleAddValue={handleAddValue}
              handleDeleteValue={handleDeleteValue}
              handleEditValue={handleEditValue}
              missingType={missingType}
            />

            <Box display="flex" gap={2} mt={4}>
              <Button
                variant="contained"
                color="success"
                onClick={handleSave}
                disabled={!!missingType}
                sx={{
                  fontWeight: 700,
                  fontSize: 18,
                  px: 5,
                  py: 1.5,
                  boxShadow: 2,
                }}
              >
                {paramId ? "Save all changes" : "Create all tags"}
              </Button>
              <Back />
            </Box>
            {!!missingType && (
              <Typography color="error" mt={2}>
                Each type must have at least one tag!
              </Typography>
            )}
          </Box>
        )
      )}
    </Box>
  );
}
